// Tekstgrootte van de open vragenlijst: drie standen, door de respondent zelf
// te kiezen. Gevraagd door SportDrenthe voor de toegankelijkheid.
//
// De waarde is een vermenigvuldiger die als CSS-variabele op de vragenlijst
// komt te staan (`--qo-tekstschaal`); elke lettergrootte in de open
// stylesheets rekent ermee. Een vermenigvuldiger en geen losse px-waarden,
// zodat de verhouding tussen kop, vraag en antwoord overal gelijk blijft.

/** Van normaal naar grootst. Niet meer standen: bij 150% past een matrixregel
 *  nog binnen de kaart, daarboven niet meer. */
const TEXT_SCALE_STEPS = [1, 1.25, 1.5];

const DEFAULT_TEXT_SCALE = TEXT_SCALE_STEPS[0];

/** Waar de keuze van de respondent blijft staan. De open vragenlijst kent geen
 *  inlog, dus er is geen profiel om hem in te bewaren. */
const TEXT_SCALE_STORAGE_KEY = 'deccos-vragenlijst-tekstschaal';

/**
 * Rondt een willekeurige waarde af op de dichtstbijzijnde geldige stand.
 *
 * Bewust géén vrije waarde: uit de opslag kan van alles komen (een oude versie
 * met andere standen, een handmatig aangepaste waarde), en een tussenwaarde
 * zou een stand opleveren die de knoppen niet kunnen tonen.
 *
 * @param {*} value - Ruwe waarde, mag alles zijn
 * @returns {number} Een van TEXT_SCALE_STEPS
 */
const clampTextScale = (value) => {
    const asNumber = Number(value);
    if (!Number.isFinite(asNumber)) return DEFAULT_TEXT_SCALE;

    return TEXT_SCALE_STEPS.reduce((closest, step) => (
        Math.abs(step - asNumber) < Math.abs(closest - asNumber) ? step : closest
    ), DEFAULT_TEXT_SCALE);
};

/**
 * De volgende stand in een richting. Op de rand blijft hij staan waar hij staat,
 * zodat 'groter' op de grootste stand niets stuks maakt.
 *
 * @param {number} current - Huidige stand
 * @param {1|-1} direction - 1 = groter, -1 = kleiner
 * @returns {number} De nieuwe stand
 */
const nextTextScale = (current, direction) => {
    const index = TEXT_SCALE_STEPS.indexOf(clampTextScale(current));
    const nextIndex = Math.min(TEXT_SCALE_STEPS.length - 1, Math.max(0, index + direction));

    return TEXT_SCALE_STEPS[nextIndex];
};

/**
 * Leest de bewaarde stand.
 *
 * De opslag kan wéigeren (privémodus, cookies uit) en gooit dan bij het lézen
 * al een fout. Dat mag de vragenlijst niet slopen — een respondent die niet kan
 * kiezen is vervelend, een respondent die niets kan invullen is erger. Vandaar
 * dat dit terugvalt op de normale stand in plaats van door te gooien.
 *
 * @param {Storage} [storage] - window.localStorage, of iets met dezelfde vorm
 * @returns {number} Een van TEXT_SCALE_STEPS
 */
const readStoredTextScale = (storage) => {
    try {
        const stored = storage?.getItem(TEXT_SCALE_STORAGE_KEY);
        if (stored === null || stored === undefined) return DEFAULT_TEXT_SCALE;
        return clampTextScale(stored);
    } catch {
        return DEFAULT_TEXT_SCALE;
    }
};

/**
 * Bewaart de stand. Lukt dat niet, dan blijft de keuze gelden voor deze sessie
 * en is hij na herladen weg — merkbaar, maar niet blokkerend.
 *
 * @param {Storage} [storage] - window.localStorage, of iets met dezelfde vorm
 * @param {number} value - De te bewaren stand
 * @returns {boolean} Of het bewaren lukte
 */
const storeTextScale = (storage, value) => {
    try {
        storage?.setItem(TEXT_SCALE_STORAGE_KEY, String(clampTextScale(value)));
        return true;
    } catch {
        return false;
    }
};

export {
    TEXT_SCALE_STEPS,
    DEFAULT_TEXT_SCALE,
    TEXT_SCALE_STORAGE_KEY,
    clampTextScale,
    nextTextScale,
    readStoredTextScale,
    storeTextScale,
};
